import { createSelector } from '@ngrx/store';
import { Params } from '@angular/router';

import {
  getLocationsSearchText,
  getLocationsSortBy,
  getSelectedFilters,
} from './location-filters.selectors';
import { getLocationMapState } from '../reducers';
import * as fromMap from '../reducers/map.reducer';
import { LocationType } from '../../services/location.service';
import { LocationInterface } from '../../models/location-interface';
import { selectRouteParams } from '../../../store';
import {
  applySelectedLocationFilters,
  filterLocationByType,
  locationsSortBy,
  searchFilter,
} from '../selectors/locations-store.utils';

export const getMapMode = createSelector(
  getLocationMapState,
  fromMap.getMapMode,
);

export const getSelectedFeature = createSelector(
  getLocationMapState,
  fromMap.getSelectedFeature,
);

export const getFeatureUnsortedList = createSelector(
  getLocationMapState,
  fromMap.getFeatureList,
);
export const getFeatureList = createSelector(
  getFeatureUnsortedList,
  (locations) =>
    [...locations].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
);

export const getLocationTypes = createSelector(
  getLocationMapState,
  fromMap.getLocationTypes,
);

export const getSelectedLocation = () =>
  createSelector(
    getFeatureList,
    selectRouteParams,
    getLocationTypes,
    (
      locations: LocationInterface[],
      routeParams: Params,
      locationTypes: LocationType[],
    ): LocationInterface | undefined => {
      const selectedLocationId = +routeParams?.id;
      if (!selectedLocationId || !locations?.length || !locationTypes?.length) {
        return undefined;
      }
      const selectedLocation = locations.find(
        (location) => location.id === selectedLocationId,
      );
      if (!selectedLocation) return undefined;
      return {
        ...selectedLocation,
        type: locationTypes.find((type) => type.id === selectedLocation.type),
      };
    },
  );

export const getLocationError = createSelector(
  getLocationMapState,
  fromMap.getLocationError,
);

export const getLocationLoading = createSelector(
  getLocationMapState,
  fromMap.getLocationLoading,
);
export const getTypesLoading = createSelector(
  getLocationMapState,
  fromMap.getTypesLoading,
);

export const getLocationTypeFilter = createSelector(
  getLocationMapState,
  fromMap.getLocationTypeFilter,
);

export const getCount = createSelector(getLocationMapState, fromMap.getCount);
export const getProgress = createSelector(
  getCount,
  getFeatureList,
  (count, locations) => {
    if (locations?.length) {
      return (locations.length / count) * 100;
    }
  },
);

export const getLocationsLoaded = createSelector(getProgress, (progress) => {
  return progress === 100;
});

// location list with all filters but without selected type
export const getLocationsWithoutTypeFilter = createSelector(
  getFeatureList,
  getLocationTypes,
  getLocationsSearchText,
  getSelectedFilters,
  getLocationsSortBy,
  (locations, types, search, locationFilters, sortBy) => {
    if (locations && types) {
      let filteredLocations = locations;
      filteredLocations = locationsSortBy(filteredLocations, sortBy);
      filteredLocations = searchFilter(filteredLocations, search, 'name');
      if (locationFilters) {
        filteredLocations = applySelectedLocationFilters(
          filteredLocations,
          locationFilters,
        );
      }
      return filteredLocations.map((location) => {
        return {
          ...location,
          type: types.find((type) => location.type === type.id),
        };
      });
    } else {
      return [];
    }
  },
);

export const getContactLocations = (contactId: number) => {
  return createSelector(getLocationsWithoutTypeFilter, (locations) => {
    return locations?.filter(
      (location) => location?.primaryContact?.id === contactId,
    );
  });
};

// location list with all filters and selected type
export const getLocationsWithTypes = createSelector(
  getLocationsWithoutTypeFilter,
  getLocationTypeFilter,
  (locations, type) => filterLocationByType(locations, type.id),
);

// The map needs the raw features (numeric `type`), so this mirrors the list's
// filtering by id. The routed location stays visible even when filtered out.
export const getFilteredFeatureList = createSelector(
  getFeatureList,
  getLocationsWithTypes,
  selectRouteParams,
  (features, filteredLocations, routeParams: Params) => {
    const visibleIds = new Set(
      filteredLocations.map((location) => location.id),
    );
    const routedLocationId = +routeParams?.id;
    return features.filter(
      (feature) =>
        visibleIds.has(feature.id) || feature.id === routedLocationId,
    );
  },
);

// location type list with counters
// todo we need this information from backend
export const getLocationTypesWithCounts = createSelector(
  getLocationTypes,
  getLocationsWithoutTypeFilter,
  (types, locations) => [
    {
      id: -1,
      name: `All locations (${locations.length})`,
      color: '#B0BEC5',
    },
    ...types.map((locationType: LocationType) => {
      return {
        ...locationType,
        name:
          locationType?.name +
          ` (${
            locations.filter(
              (location) => location?.type?.id === locationType?.id,
            ).length
          })`,
      };
    }),
  ],
);

export const getLocationTypesWithCountsById = (id: number) =>
  createSelector(getLocationTypesWithCounts, (types) =>
    types.find((type) => type.id === id),
  );

export const getHasActiveLocationFilters = createSelector(
  getLocationTypeFilter,
  getSelectedFilters,
  (locationTypeFilter, selectedFilters) =>
    (!!locationTypeFilter && locationTypeFilter.id !== -1) ||
    selectedFilters.length > 0,
);

export const getLocationTypeFilterWithCounts = createSelector(
  getLocationTypeFilter,
  getLocationsWithTypes,
  (locationType, locations) =>
    locationType.id === -1
      ? {
          id: -1,
          name: `All locations (${locations?.length})`,
          color: '#B0BEC5',
        }
      : locationType,
);

export const getLocationById = (id: number) => {
  return createSelector(
    getFeatureList,
    getLocationTypes,
    (
      list: LocationInterface[],
      types: LocationType[],
    ): LocationInterface | undefined => {
      return list
        .map((location) => {
          return {
            ...location,
            type: types.find((type) => location.type === type.id),
          };
        })
        .find((item) => item.id === +id);
    },
  );
};

export const getLocationsByType = (id: number) => {
  return createSelector(
    getFeatureList,
    getLocationTypes,
    (locations, types): LocationInterface[] => {
      if (locations && types) {
        const filteredLocations: LocationInterface[] = locations.filter(
          (location) => location.type === +id,
        );
        return filteredLocations.map((location) => {
          return {
            ...location,
            type: types.find((type) => location.type === type.id),
          };
        });
      } else {
        return [];
      }
    },
  );
};
